/**
 * @module tileUtils
 * @description Floating tile lifecycle and placement logic for the Game component.
 * Handles promoting rack tiles to floating tiles, removing them, and resolving
 * drop events onto the board or rack.
 */

/**
 * @typedef {object} AddFloatingTileDeps
 * @property {function(object): void} setFloatingTile - Promotes a tile to the floating layer.
 */

/**
 * Promotes a tile to the floating (drag) layer.
 * @param {object} tile - The tile to float.
 * @param {AddFloatingTileDeps} deps
 * @returns {void}
 */
export function addFloatingTile(tile, deps) {
    // console.log("FLOATING TILE", tile)
    deps.setFloatingTile(tile);
}

/**
 * @typedef {object} RemoveFloatingTileDeps
 * @property {function(null): void} setFloatingTile - Clears the floating tile.
 */

/**
 * Removes the current floating tile from the drag layer.
 * @param {RemoveFloatingTileDeps} deps
 * @returns {void}
 */
export function removeFloatingTile(deps) {
    deps.setFloatingTile(null);
}

/**
 * @typedef {object} FloatingTileClickHandlerDeps
 * @property {{ left: number, right: number, top: number, bottom: number } | null} boardBoundingBox - Adjusted board bounds.
 * @property {number} boardScale - Current board cell pixel size.
 * @property {object} floatingTile - The tile currently in the floating layer.
 * @property {{ left: number, right: number, top: number, bottom: number }} rackBoundingBox - Rack bounding rect.
 * @property {number} rackScale - Rack tile scale factor.
 * @property {number} rackWidth - Rack width in pixels.
 * @property {object} game - Current game state.
 * @property {Array} rackTiles - Current rack tile array.
 * @property {function(object): void} setSpaceWithBlank - Records the board space holding a blank tile.
 * @property {function(boolean): void} setShowBlankSelector - Shows/hides the blank-letter selector.
 * @property {function(object): void} setGame - Game state setter.
 * @property {function(null): void} setFloatingTile - Clears the floating tile after placement.
 * @property {function(): void} getScore - Triggers server score recalculation.
 * @property {function(object, number): void} addTileToRack - Bound rack-insertion function.
 * @property {function(): void} countPlayedTiles - Updates the played-tile count.
 * @property {boolean} isMyTurn - If false, board placement is skipped; rack return is always permitted.
 */

/**
 * Resolves a floating-tile click: places the tile on the board if the pointer is
 * within the board bounds, or returns it to the rack if within rack bounds, then
 * updates the played-tile count.
 * @param {MouseEvent} event
 * @param {FloatingTileClickHandlerDeps} deps
 * @returns {void}
 */
export function floatingTileClickHandler(event, deps) {
    const {
        boardBoundingBox,
        boardScale,
        floatingTile,
        rackBoundingBox,
        rackScale,
        rackWidth,
        game,
        rackTiles,
        setSpaceWithBlank,
        setShowBlankSelector,
        setGame,
        setFloatingTile,
        getScore,
        addTileToRack,
        countPlayedTiles,
        isMyTurn,
    } = deps;

    const x = event.clientX;
    const y = event.clientY;

    // Drop on Board
    if (
        boardBoundingBox &&
        boardBoundingBox.left <= x &&
        x <= boardBoundingBox.right &&
        boardBoundingBox.top <= y &&
        y <= boardBoundingBox.bottom
    ) {
        console.log("BOARD");
        if (!isMyTurn) return;
        let tileX = Math.floor((x - boardBoundingBox.left - boardScale / 2) / boardScale);
        let tileY = Math.floor((y - boardBoundingBox.top - boardScale / 2) / boardScale);
        console.log("TILE COORD", tileX, tileY, boardScale);
        // console.log(game.board)

        if (!game.board.rows[tileY].spaces[tileX].tile) {
            console.log("FLOATING TILE", floatingTile);
            if (!floatingTile.tile.value) {
                console.log("BLANK!");
                setSpaceWithBlank(game.board.rows[tileY].spaces[tileX]);
                setShowBlankSelector(true);
            }

            game.board.rows[tileY].spaces[tileX].tile = floatingTile;
            game.board.rows[tileY].spaces[tileX].isNew = true;
            game.board.rows[tileY].spaces[tileX].round = game.currentRound;
            game.board.rows[tileY].spaces[tileX].playerIndex = game.currentPlayer;
            console.log("CURRENT GAME", game);
            setGame({ ...game, rack: rackTiles });
            setFloatingTile(null);
            getScore();
        }
    }

    // Drop on Rack
    if (
        rackBoundingBox.left <= x &&
        x <= rackBoundingBox.right &&
        rackBoundingBox.top <= y &&
        y <= rackBoundingBox.bottom
    ) {
        console.log("RACK RACK RACK", x, rackScale, rackWidth);
        var rackX = ((x - rackBoundingBox.left) * 75 / rackScale) + rackScale / 2;
        addTileToRack(floatingTile, rackX);
        setFloatingTile(null);
    }
    countPlayedTiles();
    // console.log("FLOATING CLICK", rackBounding, boardBounding, event.clientX, event.clientY)
}
